import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Account, AccountContact } from '@/api/commercial/accounts';
import { accountApi } from '@/api/commercial/accounts';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFormError } from '@/hooks/use-form-error';
import { type Contract, ContractScope, ContractType } from '@/types/commercial/contracts';
import { ContractSummaryForm } from './contract-summary-form';
import { CONTRACT_SUMMARY_SCHEMAS, createContractSummaryValue } from './contract-summary-schema';
import { STANDARD_CONTRACT_TYPES, toApiContractType } from './contract-types';

function accountLabel(account: Account): string {
  const name = account.social_name || account.fantasy_name || account.name || 'Cliente';
  return `${name} - ${account.document?.value ?? ''}`;
}

interface ContractFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: ContractScope;
  contract?: Contract | null;
  initialType?: ContractType;
  isSaving: boolean;
  onSubmit: (values: {
    type: ContractType;
    accountId?: string;
    signerContactId: string | null;
    summary: Record<string, unknown>;
    clauses: Record<string, unknown>[];
  }) => Promise<void>;
}

export function ContractFormDialog({
  open,
  onOpenChange,
  scope,
  contract,
  initialType,
  isSaving,
  onSubmit,
}: ContractFormDialogProps) {
  const { formError, showFormError, clearFormError } = useFormError(open);
  const [type, setType] = useState<ContractType>(
    contract?.type ?? initialType ?? ContractType.SIM_CARD,
  );
  const [account, setAccount] = useState<Account | null>(null);
  const [results, setResults] = useState<Account[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [contacts, setContacts] = useState<AccountContact[]>([]);
  const [signerContactId, setSignerContactId] = useState('');
  const [summary, setSummary] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!open) return;
    const nextType = contract?.type ?? initialType ?? ContractType.SIM_CARD;
    setType(nextType);
    setAccount(null);
    setSignerContactId(contract?.signerContactId ?? '');
    setSummary(
      createContractSummaryValue(CONTRACT_SUMMARY_SCHEMAS[nextType], contract?.summary ?? {}),
    );
    if (contract?.accountId) {
      accountApi
        .findOne(contract.accountId)
        .then((selectedAccount) => {
          setAccount(selectedAccount);
          return accountApi.findContacts(selectedAccount.id);
        })
        .then(setContacts)
        .catch(() => setAccount(null));
    } else {
      setContacts([]);
    }
  }, [contract, initialType, open]);

  const searchAccounts = useMemo(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (value: string) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!value.trim()) {
          setResults([]);
          return;
        }
        setIsSearching(true);
        try {
          const response = await accountApi.findMany({ search: value, limit: 20 });
          setResults(response.docs);
        } finally {
          setIsSearching(false);
        }
      }, 400);
    };
  }, []);

  const handleAccountChange = useCallback(async (selected: Account[]) => {
    const nextAccount = selected[0] ?? null;
    setAccount(nextAccount);
    setSignerContactId('');
    setContacts(nextAccount ? await accountApi.findContacts(nextAccount.id) : []);
  }, []);

  const canSubmit =
    scope === ContractScope.STANDARD || (Boolean(account) && Boolean(signerContactId));
  const summarySchema = CONTRACT_SUMMARY_SCHEMAS[type];

  async function handleSubmit() {
    clearFormError();
    try {
      await onSubmit({
        type,
        accountId: account?.id,
        signerContactId: signerContactId || null,
        summary,
        clauses: contract?.clauses ?? [],
      });
    } catch (error) {
      showFormError(error);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{contract ? 'Editar contrato' : 'Configurar contrato'}</DialogTitle>
          <DialogDescription>
            {scope === ContractScope.STANDARD
              ? 'Configure o modelo padrão utilizado na geração dos contratos.'
              : 'Selecione o cliente e o tipo do contrato.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contract-type">Tipo</Label>
            <Select
              value={type}
              onValueChange={(value) => {
                const nextType = value as ContractType;
                const currentValue = nextType === contract?.type ? contract.summary : {};
                setType(nextType);
                setSummary(
                  createContractSummaryValue(CONTRACT_SUMMARY_SCHEMAS[nextType], currentValue),
                );
              }}
            >
              <SelectTrigger id="contract-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STANDARD_CONTRACT_TYPES.map((item) => (
                  <SelectItem key={item.id} value={toApiContractType(item.id)}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {scope === ContractScope.CLIENT && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contract-account">Cliente</Label>
                <Combobox
                  id="contract-account"
                  disableLocalFilter
                  data={results}
                  value={account ? [account] : []}
                  isLoading={isSearching}
                  placeholder="Busque por nome, CPF ou CNPJ"
                  keyExtractor={(item) => item.id}
                  displayValueGetter={accountLabel}
                  onSearchChange={searchAccounts}
                  onOptionChange={handleAccountChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contract-signer">Signatário</Label>
                <Select value={signerContactId} onValueChange={setSignerContactId}>
                  <SelectTrigger id="contract-signer">
                    <SelectValue placeholder="Selecione um contato habilitado" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts
                      .filter((contact) => contact.contractEnabled)
                      .map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {summarySchema ? (
            <ContractSummaryForm schema={summarySchema} value={summary} onChange={setSummary} />
          ) : (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              Ainda não há campos de resumo configurados para este tipo de contrato.
            </p>
          )}
        </div>

        <FormError error={formError} onDismiss={clearFormError} />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" disabled={!canSubmit || isSaving} onClick={handleSubmit}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
